import React, { useState, useEffect, useRef } from 'react';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Container,
  FormControlLabel,
  Switch,
  Chip,
  Grid,
  Checkbox,
  Link,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Visibility, VisibilityOff, Lock, Person, Cloud, Language, Close } from '@mui/icons-material';
import SidebarLanguageMenu from './SidebarLanguageMenu';
import MfaDialog from './MfaDialog';
import { useWebSocket } from './WebSocketContext';
import { apiRequest, clearApiSessionToken, setApiSessionToken } from '../Utils/apiClient';
import { useTranslation } from 'react-i18next';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { ws, isUserLoggedIn, serverUrl, setServerUrl, clearCustomServer, isConnected, connect, sessionExpired } = useWebSocket();
  const sessionCheckRequestedRef = useRef(false);
  // Custom server inputs
  const [useCustomServer, setUseCustomServer] = useState(() => !!localStorage.getItem('dataunfold_ws_url'));
  const [customServerInput, setCustomServerInput] = useState(() => localStorage.getItem('dataunfold_ws_url') || '');
  const [serverError, setServerError] = useState('');
  const [isConnectingServer, setIsConnectingServer] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(false);
  const [persistServer, setPersistServer] = useState(() => !!localStorage.getItem('dataunfold_ws_url'));
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { t, i18n } = useTranslation();
  const [languageAnchor, setLanguageAnchor] = useState(null);

  const isTargetServerCurrent = !!(
    customServerInput && serverUrl && (serverUrl.includes(customServerInput) || customServerInput.includes(serverUrl))
  );

  // Redirect if already logged in
  useEffect(() => {
    if (isUserLoggedIn) {
      navigate('/');
    }
  }, [isUserLoggedIn, navigate]);

  // If a session token exists, validate it and redirect to home when valid.
  useEffect(() => {
    if (isUserLoggedIn || sessionExpired) {
      return;
    }

    const token = localStorage.getItem('dataunfold_session_token');
    if (!token || sessionCheckRequestedRef.current) {
      return;
    }

    sessionCheckRequestedRef.current = true;
    (async () => {
      try {
        const result = await apiRequest('/api/v1/auth/validate-session', {
          method: 'POST',
          body: JSON.stringify({ session_token: token })
        });

        if (result?.success) {
          clearApiSessionToken();
          setApiSessionToken(token);
          localStorage.setItem('dataunfold_logged_in', 'true');
          navigate('/');
          return;
        }

        clearApiSessionToken();
        localStorage.setItem('dataunfold_logged_in', 'false');
      } catch (err) {
        clearApiSessionToken();
        localStorage.setItem('dataunfold_logged_in', 'false');
      } finally {
        sessionCheckRequestedRef.current = false;
      }
    })();
  }, [isUserLoggedIn, sessionExpired, navigate]);

  // Clear connecting indicator when provider reports connected
  useEffect(() => {
    if (isConnectingServer && isConnected) {
      setIsConnectingServer(false);
    }
  }, [isConnectingServer, isConnected]);

  const applyCustomServer = (persist = undefined) => {
    if (!customServerInput || !customServerInput.trim()) {
      setServerError(t('login.enter_websocket_url'));
      return;
    }
    setServerError('');
    setIsConnectingServer(true);
    const persistValue = persist === undefined ? persistServer : !!persist;
    setServerUrl(customServerInput, persistValue);
  };

  const openLanguageMenu = (event) => setLanguageAnchor(event.currentTarget);
  const closeLanguageMenu = () => setLanguageAnchor(null);
  const handleLanguageChange = (lang) => {
    try { i18n.changeLanguage(lang); } catch (e) { /* ignore */ }
    closeLanguageMenu();
  };



  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      setError(t('login.enter_both_username_password'));
      return;
    }
    // MFA will be handled in a dedicated dialog when required by the server

    setIsLoading(true);
    setError('');

    return (async () => {
      try {
        if (useCustomServer && customServerInput && customServerInput.trim()) {
          setServerError('');
          setIsConnectingServer(true);
          applyCustomServer(persistServer);
        }

        const response = await apiRequest('/api/v1/auth/login', {
          method: 'POST',
          body: JSON.stringify({
            username: username.trim(),
            password
          })
        });

        if (response?.success) {
          setUsername('');
          setPassword('');
          setMfaRequired(false);
          setError('');
          if (response.session_token) {
            setApiSessionToken(response.session_token);
          }
          localStorage.setItem('dataunfold_logged_in', 'true');
          if (response.mfa_setup_required) {
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
          window.location.href = '/';
          return;
        }

        if (response?.mfa_required) {
          setMfaRequired(true);
          const serverMsg = response?.message || '';
          if (serverMsg && serverMsg !== 'MFA code required' && serverMsg !== t('login.mfa_required')) {
            setMfaMessage(serverMsg);
          } else {
            setMfaMessage('');
          }
          setMfaMethod(response?.mfa_method || 'totp');
          setMfaCanResend(Boolean(response?.can_resend));
          setError('');
        } else {
          setMfaRequired(false);
          setError(response?.message || t('login.login_failed_check_credentials'));
        }
      } catch (err) {
        setMfaRequired(false);
        setError(err?.message || t('login.failed_send_try_again'));
      } finally {
        setIsLoading(false);
      }
    })();

    try {
      // If user wants to use a custom server, ensure it's applied and connected
  if (useCustomServer) {
        if (customServerInput && customServerInput.trim()) {
          // Apply the custom server and wait for connection
          setServerError('');
          setIsConnectingServer(true);
          applyCustomServer(persistServer);
          setPendingLogin(true);
          // Wait for the connection to establish; pendingLogin effect will send credentials
          return;
        }

        // If server is already set but socket is not open, wait for connection
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          // We rely on applyCustomServer to establish connection for custom servers
          setPendingLogin(true);
          setIsLoading(true);
          return;
        }
      }

      if (ws && ws.readyState === WebSocket.OPEN) {
        const loginData = {
          username: username.trim(),
          password: password
        };
        const loginMessage = {
          command: 'login',
          data: loginData
        };

        ws.send(JSON.stringify(loginMessage));
      } else {
        // No websocket open for the default server — connect now and send login when ready
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          setServerError('');
          setIsConnectingServer(true);
          try {
            connect();
            setPendingLogin(true);
            // Keep isLoading true until login_response
            return;
          } catch (e) {
            setError(t('login.failed_connect_server'));
            setIsLoading(false);
            setIsConnectingServer(false);
            return;
          }
        }

        setError(t('login.connection_lost'));
        setIsLoading(false);
      }
    } catch (err) {
      setError(t('login.failed_send_try_again'));
      setIsLoading(false);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // MFA dialog state
  const [mfaMessage, setMfaMessage] = useState('');
  const [mfaCanResend, setMfaCanResend] = useState(false);
  const [mfaMethod, setMfaMethod] = useState('totp');

  const handleCancelMfa = () => {
    setMfaRequired(false);
    setMfaMessage('');
    setMfaCanResend(false);
    setMfaMethod('totp');
    setError('');
  };

  // Called by MfaDialog when user submits a 6-digit code
  const onVerifyMfa = async (code) => {
    if (!code || code.length < 6) {
      setError(t('login.enter_mfa_code'));
      return;
    }
    setIsLoading(true);
    setError('');
    return (async () => {
      try {
        const response = await apiRequest('/api/v1/auth/login', {
          method: 'POST',
          body: JSON.stringify({
            username: username.trim(),
            password,
            mfa_code: code
          })
        });

        if (response?.success) {
          setUsername('');
          setPassword('');
          setMfaRequired(false);
          setError('');
          if (response.session_token) {
            setApiSessionToken(response.session_token);
          }
          localStorage.setItem('dataunfold_logged_in', 'true');
          if (response.mfa_setup_required) {
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
          window.location.href = '/';
          return;
        }

        if (response?.mfa_required) {
          setMfaRequired(true);
          const serverMsg = response?.message || '';
          if (serverMsg && serverMsg !== 'MFA code required' && serverMsg !== t('login.mfa_required')) {
            setMfaMessage(serverMsg);
          } else {
            setMfaMessage('');
          }
          setMfaMethod(response?.mfa_method || 'totp');
          setMfaCanResend(Boolean(response?.can_resend));
          setError('');
        } else {
          setError(response?.message || t('login.failed_send_try_again'));
        }
      } catch (err) {
        setError(err?.message || t('login.failed_send_try_again'));
      } finally {
        setIsLoading(false);
      }
    })();
    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        const loginData = { username: username.trim(), password, mfa_code: code };
        ws.send(JSON.stringify({ command: 'login', data: loginData }));
      } else {
        // If socket is not open, trigger connect + pending login flow
        setPendingLogin(true);
        connect();
      }
    } catch (err) {
      setError(t('login.failed_send_try_again'));
      setIsLoading(false);
    }
  };

  const handleResendMfa = () => {
    if (!mfaCanResend) {
      // Do nothing if server doesn't allow resend (e.g., TOTP)
      setError(t('login.resend_not_available') || 'Resend unavailable for this MFA method');
      return;
    }
    setError(t('login.resend_not_available') || 'Resend unavailable for this MFA method');
    return;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ command: 'resend_mfa', data: { username: username.trim() } }));
        setMfaMessage(t('login.mfa_resend_sent') || 'Verification code resent');
      } catch (e) {
        setError(t('login.failed_send_try_again'));
      }
    } else {
      setError(t('login.failed_connect_server'));
    }
  };

  const primaryColor = theme.palette.primary.main;
  const primaryLight = theme.palette.primary.light;
  

  // Convert a hex color to rgba string with alpha
  const hexToRgba = (hex, alpha = 1) => {
    if (!hex) return `rgba(0,0,0,${alpha})`;
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const bigint = parseInt(full, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Animations
  const float = keyframes`
    0% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
    100% { transform: translateY(0px); }
  `;

  const gradientShift = keyframes`
    0% { background-position: 0% 50%; filter: hue-rotate(0deg); }
    25% { background-position: 50% 20%; filter: hue-rotate(30deg); }
    50% { background-position: 100% 50%; filter: hue-rotate(60deg); }
    75% { background-position: 50% 80%; filter: hue-rotate(30deg); }
    100% { background-position: 0% 50%; filter: hue-rotate(0deg); }
  `;

  const pulse = keyframes`
    0% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(0,0,0,0)); }
    50% { transform: scale(1.06) rotate(-1deg); filter: drop-shadow(0 0 18px ${hexToRgba(primaryLight, 0.42)}); }
    100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(0,0,0,0)); }
  `;

  const starTwinkle = keyframes`
    0% { opacity: 0.15; transform: scale(0.9); }
    50% { opacity: 1; transform: scale(1.25); }
    100% { opacity: 0.15; transform: scale(0.9); }
  `;

  const scanline = keyframes`
    0% { background-position: 0 0; }
    100% { background-position: 0 300px; }
  `;

  const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  `;

  const drift = keyframes`
    0% { transform: translate(0px, 0px) scale(1); opacity: 0.9; }
    50% { transform: translate(8px, -8px) scale(1.05); opacity: 0.5; }
    100% { transform: translate(0px, 0px) scale(1); opacity: 0.9; }
  `;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        paddingBottom: isSmall ? '96px' : '0px'
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={12}
          sx={{
            background: 'white',
            borderRadius: 6,
            p: 0,
            width: { xs: '100%', md: '880px' },
            mx: 'auto',
            boxShadow: `0px 20px 52px rgba(87, 87, 176, 0.22), 0px 10px 28px rgba(87, 87, 176, 0.14)`,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
            {/* Language switcher - page level */}
            <Box sx={{ position: 'absolute', top: 30, right: 30, zIndex: 1400 }}>
              <Button size="small" variant="outlined" onClick={openLanguageMenu} startIcon={<Language />} sx={{ textTransform: 'none', borderRadius: 3 }}>
                {i18n?.language?.toUpperCase?.() || 'EN'}
              </Button>
            </Box>
            <SidebarLanguageMenu anchorEl={languageAnchor} open={!!languageAnchor} onClose={closeLanguageMenu} onLanguageChange={handleLanguageChange} primaryColor={primaryColor} lightPurple={primaryLight} />
          <Grid container>
            {/* Left brand/illustration panel */}
            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Box sx={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${hexToRgba(primaryLight, 0.95)}, ${primaryLight} 100%)`,
                backgroundSize: '200% 200%',
                animation: prefersReducedMotion ? 'none' : `${gradientShift} 6s linear infinite`,
                position: 'relative',
                width: '100%',
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#fff',
                minHeight: 320
              }}>
                <Box
                  component="img"
                  src={'/images/ai/dataunfold.png'}
                  alt="DataUnfold"
                  sx={{
                    width: 56,
                    height: 56,
                    mb: 1.5,
                    animation: prefersReducedMotion ? 'none' : `${float} 3.8s ease-in-out infinite, ${pulse} 2000ms ease-in-out infinite`,
                    transformOrigin: 'center',
                    zIndex: 1,
                    borderRadius: '8px',
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                  }}
                />
                {/* Decorative floating circles */}
                {!isSmall && (
                  <>
                    <Box sx={{ position: 'absolute', top: 8, left: -28, width: 160, height: 160, borderRadius: '50%', background: `radial-gradient(circle at 20% 20%, ${hexToRgba(primaryLight, 0.12)}, rgba(255,255,255,0) 40%)`, animation: prefersReducedMotion ? 'none' : `${drift} 9s ease-in-out infinite`, zIndex: 0 }} />
                    <Box sx={{ position: 'absolute', bottom: -12, right: -34, width: 80, height: 80, borderRadius: '50%', background: `radial-gradient(circle at 80% 60%, ${hexToRgba(primaryLight, 0.06)}, rgba(255,255,255,0) 40%)`, animation: prefersReducedMotion ? 'none' : `${drift} 12s ease-in-out infinite`, zIndex: 0 }} />
                    {/* Add extra neon beam */}
                    <Box sx={{ position: 'absolute', left: -20, top: 40, width: 6, height: 260, borderRadius: 3, background: `linear-gradient(180deg, ${hexToRgba('#fff', 0.08)}, ${hexToRgba(primaryLight, 0.28)}, ${hexToRgba(primaryColor, 0.12)})`, transform: 'skewX(-10deg)', zIndex: 0, opacity: 0.6 }} />
                  </>
                )}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 1,
                    opacity: 0,
                    animation: prefersReducedMotion ? 'none' : `${fadeIn} 700ms ease forwards`,
                    zIndex: 1,
                    textShadow: `0 4px 20px ${hexToRgba(primaryLight, 0.42)}, 0 2px 8px ${hexToRgba(primaryLight, 0.12)}`,
                  }}
                >
                  {t('login.welcome_back')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                    opacity: 0,
                    animation: prefersReducedMotion ? 'none' : `${fadeIn} 600ms ease forwards`,
                    zIndex: 1,
                  }}
                >
                  {t('login.please_log_in')}
                </Typography>
                {/* Scanline overlay */}
                {!isSmall && (
                  <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.06, backgroundImage: 'repeating-linear-gradient(to bottom, rgba(255,255,255,0.06), rgba(255,255,255,0.06) 1px, transparent 1px, transparent 6px)', animation: prefersReducedMotion ? 'none' : `${scanline} 6s linear infinite` }} />
                )}
                {/* Neon twinkle stars */}
                {!isSmall && (
                  <>
                    {[
                      { left: '8%', top: '18%', size: 6, delay: '0s' },
                      { left: '24%', top: '10%', size: 8, delay: '0.4s' },
                      { left: '40%', top: '28%', size: 5, delay: '1s' },
                      { left: '68%', top: '14%', size: 7, delay: '0.7s' },
                      { left: '86%', top: '30%', size: 4, delay: '1.2s' },
                    ].map((s, i) => (
                      <Box key={i} sx={{ position: 'absolute', left: s.left, top: s.top, width: s.size, height: s.size, borderRadius: '50%', background: `linear-gradient(180deg, ${hexToRgba('#fff', 1)}, ${hexToRgba(primaryLight, 0.7)})`, boxShadow: `0 0 14px ${hexToRgba(primaryLight, 0.22)}`, transform: 'translateZ(0)', animation: prefersReducedMotion ? 'none' : `${starTwinkle} ${5 + (i % 3)}s ${s.delay} ease-in-out infinite`, zIndex: 0 }} />
                    ))}
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ p: isSmall ? 3 : 5 }}>
          {/* Header (visible on small screens) */}
              <Box sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3 }}>
                <Box sx={{ display: { xs: 'inline-flex', md: 'none' }, alignItems: 'center', justifyContent: 'center', width: 64, height: 64, borderRadius: '50%', backgroundColor: primaryLight + '20', mb: 2, border: `2px solid ${primaryColor}20` }}>
                  <Lock sx={{ fontSize: 32, color: primaryColor }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: primaryColor, mb: 0.5 }}>
                  {t('login.welcome_back')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>{t('login.please_log_in')}</Typography>
              </Box>

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box
            component="div"
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
          >
            {/* Optional custom server toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <FormControlLabel
                control={<Switch checked={useCustomServer} onChange={(e) => {
                  const checked = e.target.checked;
                  setUseCustomServer(checked);
                  if (!checked) {
                    // Reset to default server when toggled off
                    setCustomServerInput('');
                    try { clearCustomServer(); } catch (e) { /* ignore */ }
                  }
                }} />}
                label={<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{t('login.connections_optional')}</Typography>}
              />
            </Box>

            {useCustomServer && (
                <Box sx={{ 
                p: isSmall ? 1.5 : 2, 
                borderRadius: 2, 
                border: `1px solid ${primaryColor}20`,
                backgroundColor: primaryLight + '08',
                boxShadow: '0 2px 8px rgba(87, 87, 176, 0.08)'
              }}>
                <Grid container spacing={isSmall ? 1.5 : 2}>
                  {/* Server input */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('login.websocket_server')}
                      placeholder={t('login.websocket_server_placeholder')}
                      value={customServerInput}
                      onChange={(e) => setCustomServerInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCustomServer(); } }}
                      size={isSmall ? 'small' : 'medium'}
                      disabled={isConnectingServer}
                      error={!!serverError}
                      helperText={serverError || t('login.current_server', { server: serverUrl })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Cloud sx={{ color: primaryColor }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: primaryColor,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: primaryColor,
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Action buttons */}
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color={isConnected && isTargetServerCurrent ? 'success' : 'primary'}
                      size="medium"
                      onClick={() => applyCustomServer()}
                      disabled={!customServerInput || isConnectingServer || (isConnected && isTargetServerCurrent)}
                      fullWidth
                      sx={{ minHeight: 44 }}
                    >
                      {isConnectingServer ? t('login.connecting') : (isConnected && isTargetServerCurrent ? t('login.connected') : t('login.connect'))}
                    </Button>
                  </Grid>

                  {/* Persist checkbox and status */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <FormControlLabel
                        control={<Checkbox checked={persistServer} onChange={(e) => setPersistServer(e.target.checked)} size="small" />}
                        label={<Typography variant="body2" sx={{ fontSize: isSmall ? '0.85rem' : '0.875rem' }}>{t('login.remember_for_next_time')}</Typography>}
                      />
                      {isConnectingServer ? (
                        <CircularProgress size={20} />
                      ) : (
                          <Chip 
                          label={isConnected ? t('login.connected') : t('login.disconnected')} 
                          color={isConnected ? 'success' : 'default'} 
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                    </Box>
                  </Grid>

                  {/* Pending login alert */}
                  {pendingLogin && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        {isConnectingServer ? t('login.connecting_send_automatically') : t('login.waiting_send_automatically')}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
            {/* Username Field */}
              <TextField
              fullWidth
              size={isSmall ? 'small' : 'medium'}
              label={t('login.username')}
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleLogin();
                }
              }}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: primaryColor }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: primaryColor,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: primaryColor,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: primaryColor,
                },
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              size={isSmall ? 'small' : 'medium'}
              label={t('login.password')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleLogin();
                }
              }}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: primaryColor }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleShowPassword}
                      disabled={isLoading}
                      sx={{ color: primaryColor }}
                      aria-label={showPassword ? t('login.hide_password') : t('login.show_password')}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: primaryColor,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: primaryColor,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: primaryColor,
                },
              }}
            />

            {/* MFA dialog extracted to `MfaDialog` component */}
            <MfaDialog
              open={mfaRequired}
              onClose={handleCancelMfa}
              onVerify={onVerifyMfa}
              onResend={handleResendMfa}
              canResend={mfaCanResend}
              method={mfaMethod}
              message={mfaMessage}
              isLoading={isLoading}
            />

            {/* Pending-login info for default server */}
            {pendingLogin && !useCustomServer && (
              <Alert severity="info" sx={{ mb: 1 }}>{t('login.connecting_will_send_when_connected')}</Alert>
            )}

            {/* Login Button */}
            <Button
              fullWidth
              type="button"
              onClick={handleLogin}
              variant="contained"
              size={isSmall ? 'medium' : 'large'}
              disabled={isLoading || !username.trim() || !password.trim()}
              sx={{
                backgroundColor: primaryColor,
                borderRadius: 2,
                py: isSmall ? 1 : 1.5,
                fontSize: isSmall ? '0.95rem' : '1rem',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: isSmall ? 44 : 48,
                boxShadow: `0px 4px 12px rgba(87, 87, 176, 0.3)`,
                '&:hover': {
                  backgroundColor: '#4a4a9d',
                  boxShadow: `0px 6px 16px rgba(87, 87, 176, 0.4)`,
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  boxShadow: 'none',
                },
              }}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isLoading ? t('login.logging_in') : t('login.log_in')}
            </Button>
          </Box>

          {/* Footer (in-dialog info) */}
          <Box sx={{ textAlign: 'center', mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
            <Typography variant="body2" sx={{ color: '#888', fontSize: '0.85rem' }}>
              {t('login.secure_authentication_required')}
            </Typography>
          </Box>
        </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
      {/* Global bottom footer (outside the login dialog) - attached appearance */}
      <Box sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1200, pointerEvents: 'none' }}>
        <Box sx={{ backgroundColor: 'rgba(255,255,255,0.96)', borderTop: '1px solid #eee', py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, pointerEvents: 'auto' }}>
          <img
            src={'/images/ai/dataunfold.png'}
            alt="DataUnfold"
            style={{ width: 18, height: 18, objectFit: 'contain', opacity: 0.95 }}
          />
          <Typography variant="caption" sx={{ color: '#666', fontSize: '0.82rem', fontWeight: 500 }}>
            {t('login.powered_by_prefix')}&nbsp;
            <Link href="https://www.dataunfold.com" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600, color: 'inherit' }}>
              dataunfold
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
